import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { AccountFeatureState } from './account.types';
import { UsernameService } from './username.service';
import {
  ChartTypes,
  FeatureFlag,
  FeatureFlagsFacade,
  OrgTypes,
  PreferenceTypes,
  UserTypes,
} from '@mode/shared/contract-common';
import * as UserSelectors from './user/user.selectors';
import { filter, map, startWith, withLatestFrom } from 'rxjs/operators';
import * as PreferenceSelectors from './preference/preference.selectors';
import * as OrgSelectors from './org/org.selectors';
import * as PaletteSelectors from './color-palette/color-palette.selectors';
import * as AdminSelectors from './admin/admin.selectors';
import * as EmailAdminSelectors from './email-admin/email-admin.selectors';
import * as ViewedReportIdsSelectors from './viewed-report-ids/viewed-report-ids.selectors';
import { DEFAULT_CHART_FORMAT, isPresent } from '@mode/shared/util-js';
import * as UserActions from './user/user.actions';
import * as PreferenceActions from './preference/preference.actions';
import * as OrgActions from './org/org.actions';
import * as ColorPaletteActions from './color-palette/color-palette.actions';
import * as AdminActions from './admin/admin.actions';
import * as CollectionActions from './collection/collection.actions';
import * as ViewedReportIdsActions from './viewed-report-ids/viewed-report-ids.actions';
import * as EmailAdminActions from './email-admin/email-admin.actions';
import { AccountFacade } from '@mode/accounts/contract';

/**
 * NOTE before using. Do not use both AccountFacade and legacy client.reducers state from
 * the same set of components, choose one or the other. That generally means new Angular
 * components or refactored AngularJS flows can use this Facade while most legacy code
 * will continue using the existing NgRx client state.
 */

@Injectable()
export class AccountFacadeService implements AccountFacade {
  hasStudioSeatLimit$: Observable<boolean>;
  hasOrgSeatLimitReached$: Observable<boolean>;
  hasOrganizationRestricted$: Observable<boolean>;
  canCreateCollection$: Observable<boolean>;

  constructor(
    private store: Store<AccountFeatureState>,
    private usernameService: UsernameService,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.user$ = store.select(UserSelectors.selectSelf).pipe(filter(isPresent));
    this.userPreference$ = store.select(PreferenceSelectors.selectPreferences).pipe(filter(isPresent));
    this.currentOrg$ = store.select(OrgSelectors.selectCurrent).pipe(filter(isPresent));
    this.reportOwner$ = store.select(OrgSelectors.selectReport).pipe(filter(isPresent));
    this.reportOwnerColorPalettes$ = store.select(PaletteSelectors.selectPalettes).pipe(filter(isPresent));
    this.reportOwnerThemeSettingsLink$ = store
      .select(OrgSelectors.selectReportThemeSettingsLink)
      .pipe(filter(isPresent));
    this.reportOwnerAccountDatasetLimit$ = store
      .select(OrgSelectors.selectReport)
      .pipe(map((report) => (report?.datasetSizeLimitMb ? report.datasetSizeLimitMb * 1000000 : undefined)));
    this.userOrgs$ = store.select(UserSelectors.selectUserOrgs).pipe(
      filter(isPresent),
      withLatestFrom(this.currentOrg$),
      map(([orgs, current]) => {
        // Sort so currently select org is at the top, otherwise alphabetically
        return orgs.slice(0).sort((a, b) => {
          if (a.token === current.token || a.displayName < b.displayName) {
            return -1;
          }

          if (b.token === current.token || a.displayName > b.displayName) {
            return 1;
          }

          return 0;
        });
      })
    );

    this.isUserOrgsLoading$ = store.select(UserSelectors.selectIsUserOrgsLoading);
    this.isUserOrgsComplete$ = store.select(UserSelectors.selectIsUserOrgsComplete);
    this.hasMemberOrgs$ = this.user$.pipe(map((user) => (user.isAnonymous === false ? user.orgCount > 0 : false)));

    this.isAdminsLoading$ = store.select(AdminSelectors.selectIsLoading);
    this.isAdminsComplete$ = store.select(AdminSelectors.selectIsComplete);
    this.admins$ = store.select(AdminSelectors.selectAdmins);

    this.isEmailAdminSending$ = store.select(EmailAdminSelectors.selectIsSending);
    this.isEmailAdminSuccess$ = store.select(EmailAdminSelectors.selectIsSuccess);
    this.isEmailAdminError$ = store.select(EmailAdminSelectors.selectError);

    this.isViewedReportIdsComplete$ = store.select(ViewedReportIdsSelectors.selectIsComplete);
    this.viewedReportIds$ = store.select(ViewedReportIdsSelectors.selectViewedReportIds);

    const defaultPalettes$ = store.select(PaletteSelectors.selectDefaultPalettes);

    this.defaultCategoricalPalette$ = defaultPalettes$.pipe(
      map((defaults) => defaults.categorical),
      filter(isPresent)
    );

    this.defaultDivergentPalette$ = defaultPalettes$.pipe(
      map((defaults) => defaults.divergent),
      filter(isPresent)
    );

    this.defaultSequentialPalette$ = defaultPalettes$.pipe(
      map((defaults) => defaults.sequential),
      filter(isPresent)
    );

    this.defaultCategoricalChartFormat$ = this.defaultCategoricalPalette$.pipe(
      map((palette) => ({
        ...DEFAULT_CHART_FORMAT,
        colors: { ...DEFAULT_CHART_FORMAT.colors, theme: palette.token, custom: palette.value?.split(',') },
      }))
    );

    this.defaultDivergentChartFormat$ = this.defaultDivergentPalette$.pipe(
      map((palette) => ({
        ...DEFAULT_CHART_FORMAT,
        colors: { ...DEFAULT_CHART_FORMAT.colors, theme: palette.token, custom: palette.value?.split(',') },
      }))
    );

    this.defaultSequentialChartFormat$ = this.defaultSequentialPalette$.pipe(
      map((palette) => ({
        ...DEFAULT_CHART_FORMAT,
        colors: { ...DEFAULT_CHART_FORMAT.colors, theme: palette.token, custom: palette.value?.split(',') },
      }))
    );

    this.isLoggedIn$ = store.select(UserSelectors.selectLoggedIn);

    this.hasStudioSeatLimit$ = this.currentOrg$.pipe(
      map((org) => !!org?.isStudioSeatLimitExceed),
      startWith(false)
    );

    this.hasOrgSeatLimitReached$ = combineLatest([this.currentOrg$]).pipe(
      map(([org]) => !!org?.orgSeatLimitReached),
      startWith(false)
    );
    this.hasOrganizationRestricted$ = combineLatest([this.currentOrg$]).pipe(
      map(([org]) => !!org?.organizationRestricted),
      startWith(false)
    );

    this.canCreateCollection$ = store.select(OrgSelectors.selectCanCreateOrgCollection).pipe(
      filter(isPresent),
      withLatestFrom(this.featureFlagsFacade.asObservable(FeatureFlag.CollectionCreationRestriction)),
      map(([canCreate, enabled]) => !enabled || canCreate)
    );
  }

  user$: Observable<UserTypes.User>;
  userPreference$: Observable<PreferenceTypes.PreferenceState>;
  currentOrg$: Observable<OrgTypes.Organization>;
  reportOwner$: Observable<OrgTypes.Organization>;
  reportOwnerColorPalettes$: Observable<ReadonlyArray<OrgTypes.ColorPalette>>;
  reportOwnerThemeSettingsLink$: Observable<string>;
  reportOwnerAccountDatasetLimit$: Observable<number | undefined>;
  isLoggedIn$: Observable<boolean>;

  userOrgs$: Observable<ReadonlyArray<UserTypes.UserOrganization>>;
  isUserOrgsLoading$: Observable<boolean>;
  isUserOrgsComplete$: Observable<boolean>;
  hasMemberOrgs$: Observable<boolean>;

  defaultCategoricalPalette$: Observable<OrgTypes.ColorPalette>;
  defaultDivergentPalette$: Observable<OrgTypes.ColorPalette>;
  // Note, there is currently no way to create a sequential palette
  defaultSequentialPalette$: Observable<OrgTypes.ColorPalette>;

  defaultCategoricalChartFormat$: Observable<ChartTypes.ChartFormat>;
  defaultDivergentChartFormat$: Observable<ChartTypes.ChartFormat>;
  defaultSequentialChartFormat$: Observable<ChartTypes.ChartFormat>;
  admins$: Observable<ReadonlyArray<OrgTypes.Admin>>;
  isAdminsLoading$: Observable<boolean>;
  isAdminsComplete$: Observable<boolean>;
  isEmailAdminSending$: Observable<boolean>;
  isEmailAdminSuccess$: Observable<boolean>;
  isEmailAdminError$: Observable<Error | null>;
  viewedReportIds$: Observable<number[]>;
  isViewedReportIdsComplete$: Observable<boolean>;

  private requireUsername(username: string | null): string {
    if (username == null) {
      throw Error('username is empty'); // TODO: Log to bugsnag
    }

    return username;
  }

  /**
   * Sets the current username for the application and loads that user.
   * If no username is provided, it will set the state to an anonymous
   * user.
   */
  loadCurrentUser(username: string | null) {
    this.usernameService.setUser(username);
    if (username != null) {
      this.store.dispatch(UserActions.loadCurrentUser({ username }));
    } else {
      this.store.dispatch(UserActions.loadAnonymousUser());
    }
  }

  loadUserOrgs() {
    const username = this.usernameService.getUser();
    if (username != null) {
      this.store.dispatch(UserActions.loadUserOrgs({ username }));
    }
  }

  loadUserPreferences() {
    const username = this.usernameService.getUser();
    if (username != null) {
      this.store.dispatch(PreferenceActions.loadPreferences({ username }));
    }
  }

  /**
   * Load the currently selected org (upper left org selector).
   */
  loadCurrentOrg(username: string) {
    // TODO: More explicit catching of this error state
    this.requireUsername(username);

    this.usernameService.setOrg(username);
    this.store.dispatch(OrgActions.loadCurrentOrg({ username }));
  }

  /**
   * Load the organization that owns the report/exploration. Most of the time this will also end
   * up equal to the current org. One exception is if the user is viewing a report owned by an org
   * they are not a member of.
   */
  loadReportOwner(username: string) {
    // TODO: More explicit catching of this error state
    this.requireUsername(username);

    this.usernameService.setReportOwner(username);
    this.store.dispatch(OrgActions.loadReportOwner({ username }));
  }

  /**
   * Load/refresh the report owner color palettes. Don't use on initial load, unless you
   * can guarantee report owner is loaded when you call this.
   * @deprecated should rely on the loadReportOwnerSuccess effect instead
   */
  loadReportOwnerColorPalettes() {
    const username = this.usernameService.getReportOwner();
    if (username != null) {
      this.store.dispatch(ColorPaletteActions.loadColorPalettes({ username }));
    }
  }

  loadAdminsForEmailForm() {
    const username = this.usernameService.getOrg();
    if (username) {
      this.store.dispatch(AdminActions.loadAdmins({ username }));
    }
  }

  loadCollections() {
    const username = this.usernameService.getOrg();
    if (username) {
      this.store.dispatch(CollectionActions.loadCollections({ username }));
    }
  }

  refreshCollections() {
    const username = this.usernameService.getOrg();
    if (username) {
      this.store.dispatch(CollectionActions.refreshCollections({ username }));
    }
  }

  loadViewedReportIds(username: string) {
    if (username) {
      this.store.dispatch(ViewedReportIdsActions.loadViewedReportIds({ username }));
    }
  }

  emailAdmin(adminId: number, message: string): void {
    const username = this.usernameService.getOrg();
    if (username) {
      this.store.dispatch(EmailAdminActions.emailAdmin({ username, adminId, message }));
    }
  }
}
